import React, { useEffect, useMemo, useState } from "react";
import { Card, Spinner, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../App.css";
import { IoMdSettings } from "react-icons/io";
import { RiInboxUnarchiveFill } from "react-icons/ri";
import { FaArrowRight } from "react-icons/fa";
import "../Styles/ModulePage.css";
import ModuleAccessSummary from "./ModuleAccessSummary";

const CardItem = ({
  image,
  title,
  subtitle,
  buttonText,
  onButtonClick,
  onArchiveClick,
  allowedUserCount,
  allowedUserNames,
}) => (
  <Card className="module-card">
    <div className="module-card__media">
      <Card.Img variant="top" src={image} className="module-card__image" />
      <span className="module-card__badge module-card__badge--archived">Archived</span>
      <Dropdown
        className="position-absolute"
        style={{ top: "10px", right: "10px" }}
      >
        <Dropdown.Toggle
          variant="light"
          id={`archived-module-menu-${title}`}
          className="module-card__menu-button"
          aria-label={`Actions for ${title}`}
        >
          <IoMdSettings size={20} />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item onClick={onArchiveClick}>
            <RiInboxUnarchiveFill /> &nbsp; Unarchive
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>

    <Card.Body className="module-card__body">
      <Card.Title className="module-card__title">{title}</Card.Title>
      <Card.Subtitle className="module-card__subtitle">
        {subtitle}
      </Card.Subtitle>
      <ModuleAccessSummary count={allowedUserCount} names={allowedUserNames} />

      <button
        className="module-card__button"
        onClick={onButtonClick}
      >
        <span>{buttonText}</span><FaArrowRight aria-hidden="true" />
      </button>
    </Card.Body>
  </Card>
);

function Archive() {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));

        if (!user || !user.user_id) {
          console.error("User not found or user ID missing in localStorage");
          setLoading(false);
          return;
        }

        const userId = user.user_id;

        const response = await fetch(
          `${process.env.REACT_APP_API}/getArchiveModule?user_id=${userId}`,
        );

        const data = await response.json();

        if (data.success) {
          setModules(data.data || []);
        } else {
          console.error("Error fetching modules:", data.message);
        }
      } catch (error) {
        console.error("Error fetching modules:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, []);

  const handleAssessmentClick = (moduleId, moduleName) => {
    navigate("/assessmentpage", { state: { moduleId, moduleName } });
  };

  const handleArchive = async (moduleId) => {
    const confirm = window.confirm(
      "Are you sure you want to unarchive this module?",
    );
    if (!confirm) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API}/setUnarchive`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ module_id: moduleId }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setModules((prev) => {
          const updated = prev.filter((m) => m.module_id !== moduleId);

          // if current page becomes empty after removing item, go back one page
          const newTotalPages = Math.ceil(updated.length / itemsPerPage) || 1;
          if (currentPage > newTotalPages) {
            setCurrentPage(newTotalPages);
          }

          return updated;
        });
      } else {
        console.error("Failed to unarchive:", data.message);
      }
    } catch (error) {
      console.error("Error unarchiving module:", error);
    }
  };

  const totalPages = Math.ceil(modules.length / itemsPerPage);

  const currentModules = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return modules.slice(startIndex, endIndex);
  }, [modules, currentPage]);


  if (loading) {
    return (
      <div className="module-grid__state">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      {modules.length === 0 ? (
        <div className="module-grid__empty module-grid__empty--archive">
          <div>
          <h5>No archived modules found</h5>
          <p>
            There is no archived module to display.
          </p>
          </div>
        </div>
      ) : (
        <>
          <div className="module-grid">
            {currentModules.map((module) => (
              <div key={module.module_id}>
                <CardItem
                  image="https://images.unsplash.com/photo-1488375634201-b85b28653a79?q=80&w=2029&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  title={module.module_name}
                  subtitle={module.description}
                  buttonText="Learn More"
                  onButtonClick={() =>
                    handleAssessmentClick(module.module_id, module.module_name)
                  }
                  onArchiveClick={() => handleArchive(module.module_id)}
                  allowedUserCount={module.allowed_user_count}
                  allowedUserNames={module.allowed_user_names}
                />
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <nav aria-label="Page navigation" className="mt-4 mb-3">
              <ul className="pagination justify-content-center flex-wrap gap-1">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    style={{ borderRadius: "10px" }}
                  >
                    Previous
                  </button>
                </li>

                {currentPage > 3 && (
                  <>
                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(1)}
                        style={{ borderRadius: "10px" }}
                      >
                        1
                      </button>
                    </li>

                    {currentPage > 4 && (
                      <li className="page-item disabled">
                        <span
                          className="page-link"
                          style={{ borderRadius: "10px" }}
                        >
                          ...
                        </span>
                      </li>
                    )}
                  </>
                )}

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (page) =>
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 2 && page <= currentPage + 2),
                  )
                  .map((page) => (
                    <li
                      key={page}
                      className={`page-item ${page === currentPage ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(page)}
                        style={{
                          borderRadius: "10px",
                          background: page === currentPage ? "#263F86" : "#fff",
                          borderColor:
                            page === currentPage ? "#263F86" : "#EEF0F4",
                          color: page === currentPage ? "#fff" : "#263F86",
                          fontWeight: 700,
                        }}
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                {currentPage < totalPages - 2 && (
                  <>
                    {currentPage < totalPages - 3 && (
                      <li className="page-item disabled">
                        <span
                          className="page-link"
                          style={{ borderRadius: "10px" }}
                        >
                          ...
                        </span>
                      </li>
                    )}

                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(totalPages)}
                        style={{ borderRadius: "10px" }}
                      >
                        {totalPages}
                      </button>
                    </li>
                  </>
                )}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    style={{ borderRadius: "10px" }}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

export default Archive;
